import { clearChatOpenPayload, loadMoreMessages } from 'app-components/flux/ai-chat';
import type { AiChatHiddenContext, ChartRequest } from 'app-components/flux/ai-chat/aiChatTypes';

import * as aiChatSelectors from 'app-components/flux/ai-chat/aiChatSelectors';

import { getLogger } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useEffect, useRef, useMemo, useCallback, type JSX } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { makeStyles } from 'tss-react/mui';
import { randomGreeting } from './utils';
import ChatFeedback from './ChatFeedback';
import ChatDefaultState from './ChatDefaultState';
import { createMarkdownComponents } from 'utils/createMarkdownComponents';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { profileSelectors } from 'app-components/flux/profile';
import AiLogoIcon from 'assets/icons/quicken-ai/ai-logo-icon.inline.svg';
import { useChatSession } from 'components/ChatLauncher/chatSession';
import type { ChatViewMode } from 'components/ChatLauncher/ChatDockContext';
import ChartPreviewDialog from 'components/Dialogs/ChartPreviewDialog';
// import { useVoiceRecorder } from 'hooks/useVoiceRecorder';
import ChatControls from './ChatControls';
import ChatHeader from './ChatHeader';
import ChatSideBar from './ChatSideBar';
import ReasoningBlock from './ReasoningBlock';
import ChatMessageChartCard from './chartPreviews/ChatMessageChartCard';
import { getEffectiveFullscreenPanelWidth, getFullscreenSidebarLayoutMode } from './fullscreenChatLayout';
import useMeasure from 'hooks/useMeasure';

const log = getLogger('SimpleChat');

const TOP_SCROLL_THRESHOLD = 80;
const BOTTOM_SCROLL_THRESHOLD = 100;

const useStyles = makeStyles<{ viewMode: ChatViewMode }>({
  name: 'AI-Chat',
})((theme, { viewMode }) => {
  const isDocked = viewMode === 'docked';
  const isExpanded = viewMode === 'expanded';
  const isFloating = viewMode === 'floating';

  return {
    dialogBorder: {
      background: theme.palette.ai.background,
      borderRadius: isDocked ? 0 : 12,
      width: isFloating ? 500 : '100%',
      height: isFloating ? '80vh' : '100%',
      padding: isDocked ? 0 : 8,
      backdropFilter: 'blur(4px)',
      zIndex: theme.zIndex.modal - 1,
      transition: 'all 0.3s ease-in-out',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: isDocked ? theme.shadows[0] : theme.shadows[2],
      ...(isDocked || isExpanded
        ? {
            position: 'relative',
          }
        : {
            position: 'relative',
            right: theme.spacing(3),
          }),
    },
    paper: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light} 100%)`,
      backdropFilter: 'blur(16px)',
      overflow: 'hidden',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    messageList: {
      paddingTop: theme.spacing(10),
      paddingBottom: theme.spacing(14),
      paddingLeft: theme.spacing(2.5),
      paddingRight: theme.spacing(2.5),
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      borderRadius: theme.shape.borderRadius,
      userSelect: 'text',
    },
    messageListDefaultState: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      paddingBottom: theme.spacing(2.5),
    },
    messageBoxUser: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: theme.spacing(1.5, 0),
    },
    messageBoxAi: {
      display: 'flex',
      textAlign: 'left',
      margin: theme.spacing(1.5, 0),
    },
    messageBubbleUser: {
      maxWidth: '85%',
      display: 'inline-block',
      padding: theme.spacing(1),
      backgroundColor: theme.palette.primary.light,
      borderRadius: '12px 4px 12px 12px',
      marginLeft: theme.spacing(5),
    },
    messageBubbleAi: {
      flex: 1,
      minWidth: 0,
      maxWidth: '100%',
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius * 2,
    },
    aiAvatarColumn: {
      flexShrink: 0,
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(2),
      zIndex: 0,
    },
    streamingMessageBox: {
      display: 'flex',
      textAlign: 'left',
      margin: theme.spacing(1.5, 0),
      width: '100%',
      alignItems: 'flex-start',
    },
    paginationRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 0),
      color: theme.palette.text.secondary,
    },
    paginationError: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 0),
    },
    scrollToBottom: {
      position: 'absolute',
      left: '50%',
      bottom: theme.spacing(12),
      transform: 'translateX(-50%)',
      zIndex: 2,
    },
    scrollToBottomButton: {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.border.light}`,
      boxShadow: theme.shadows[2],
    },
  };
});

type SimpleChatProps = {
  handleMinimize: () => void;
  onViewModeChange: (mode: ChatViewMode) => void;
  viewMode: ChatViewMode;
  onClose: () => void;
  dragHandleRef?: React.RefObject<HTMLDivElement | null>;
  open?: boolean;
  isMinimized?: boolean;
  greeting?: string;
};

const AICHat = ({
  handleMinimize,
  onViewModeChange,
  viewMode,
  onClose,
  dragHandleRef,
  open: _open,
  isMinimized: _isMinimized,
  greeting: greetingProp,
}: SimpleChatProps): JSX.Element => {
  const [input, setInput] = React.useState('');
  const [showSideBarMenu, setShowSideBarMenu] = React.useState(viewMode === 'expanded');
  const [paginationError, setPaginationError] = React.useState<string | null>(null);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = React.useState(true);
  const [activeChart, setActiveChart] = React.useState<ChartRequest | null>(null);
  const { classes, theme } = useStyles({ viewMode });
  const persistHistorySidebar = viewMode === 'docked' || viewMode === 'expanded';
  const isFullscreen = viewMode === 'expanded';
  const [panelRef, { width: panelWidth = 0 }] = useMeasure() as [
    (element: HTMLDivElement | null) => void,
    { width?: number },
  ];
  const fullscreenPanelWidth = isFullscreen ? getEffectiveFullscreenPanelWidth(panelWidth) : 0;
  const fullscreenSidebarLayout = isFullscreen ? getFullscreenSidebarLayoutMode(fullscreenPanelWidth) : null;
  const usePersistentFullscreenDrawer = fullscreenSidebarLayout === 'persistent';
  const useTemporaryFullscreenDrawer = fullscreenSidebarLayout === 'temporary';
  const shouldAutoCloseFullscreenSidebar = fullscreenSidebarLayout === 'hidden';
  const prevViewModeRef = useRef(viewMode);
  const userClosedFullscreenSidebarRef = useRef(false);

  const handleSetShowSideBarMenu = useCallback(
    (value: React.SetStateAction<boolean>) => {
      setShowSideBarMenu((previous) => {
        const next = typeof value === 'function' ? value(previous) : value;
        if (viewMode === 'expanded' && !next) {
          userClosedFullscreenSidebarRef.current = true;
        }
        return next;
      });
    },
    [viewMode],
  );

  const handleCloseSideBarMenu = useCallback(() => {
    handleSetShowSideBarMenu(false);
  }, [handleSetShowSideBarMenu]);

  useEffect(() => {
    const enteredFullscreen = viewMode === 'expanded' && prevViewModeRef.current !== 'expanded';
    prevViewModeRef.current = viewMode;

    if (enteredFullscreen) {
      userClosedFullscreenSidebarRef.current = false;
    } else if (viewMode !== 'expanded') {
      userClosedFullscreenSidebarRef.current = false;
    }
  }, [viewMode]);

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }

    if (shouldAutoCloseFullscreenSidebar) {
      setShowSideBarMenu(false);
      return;
    }

    if (!userClosedFullscreenSidebarRef.current) {
      setShowSideBarMenu(true);
    }
  }, [isFullscreen, shouldAutoCloseFullscreenSidebar, fullscreenSidebarLayout]);

  const dispatch = useAppDispatch();

  const { firstName = '' } = useAppSelector((state) => profileSelectors.getProfile(state)) || {};
  const fallbackGreeting = useMemo(() => randomGreeting(), []);
  const greeting = greetingProp ?? fallbackGreeting;

  const { conversationId } = useChatSession();
  const hasMore = useAppSelector((state) =>
    conversationId ? aiChatSelectors.selectConversationHasMore(state, conversationId) : false,
  );
  const isLoadingMore = useAppSelector((state) =>
    conversationId ? aiChatSelectors.selectConversationIsLoadingMore(state, conversationId) : false,
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const isInitialLoadRef = useRef(true);
  const autoScrollEnabledRef = useRef(true);

  const {
    messages,
    streamingMessage,
    reasoningMessage,
    isConversationLoading,
    sendMessage,
    isReady,
    error,
    startNewChat,
  } = useChatSession();
  const chatOpenPayload = useAppSelector(aiChatSelectors.getChatOpenPayload);
  const [pendingAutoSendMessage, setPendingAutoSendMessage] = React.useState<string | null>(null);
  const [pendingHiddenContext, setPendingHiddenContext] = React.useState<AiChatHiddenContext | null>(null);
  const [pendingEnableWebSearch, setPendingEnableWebSearch] = React.useState<boolean | undefined>(undefined);

  const isStreaming = useMemo(() => streamingMessage.length > 0, [streamingMessage]);
  const localDragHandleRef = useRef<HTMLDivElement | null>(null);
  const handleRef = dragHandleRef ?? localDragHandleRef;
  const isLoading = isLoadingMore && !streamingMessage;
  const isPaginating = isLoadingMore && prevScrollHeightRef.current != null;
  const lastMessage = messages[messages.length - 1];
  const isWaitingForResponse = messages.length > 0 && (isConversationLoading || lastMessage?.role === 'user');
  const showStatus = (isWaitingForResponse || Boolean(reasoningMessage)) && !streamingMessage;

  // Default state: when conversation hasn't started (no messages, not streaming, not loading, not showing status)
  const isDefaultState = useMemo(
    () => messages.length === 0 && !streamingMessage && !isLoading && !showStatus,
    [messages.length, streamingMessage, isLoading, showStatus],
  );
  const showScrollToBottom = !isDefaultState && !isAutoScrollEnabled;

  const markdownComponents = useMemo(() => createMarkdownComponents(theme), [theme]);

  useEffect(() => {
    if (!chatOpenPayload) return;
    const { conversationMode } = chatOpenPayload;
    const shouldStartNew = conversationMode === 'new';
    const message = chatOpenPayload.message?.trim();

    if (shouldStartNew) {
      startNewChat().catch((err: unknown) => {
        log.error('AIChat: Failed to start new conversation for open payload', err);
      });
    }

    if (message) {
      if (chatOpenPayload.autoSend) {
        setPendingAutoSendMessage(message);
        setPendingHiddenContext(chatOpenPayload.hiddenContext ?? null);
        setPendingEnableWebSearch(chatOpenPayload.enableWebSearch);
      } else if (!input.trim()) {
        setInput(message);
      }
    }
    dispatch(clearChatOpenPayload());
  }, [chatOpenPayload, dispatch, input, startNewChat]);

  useEffect(() => {
    if (!pendingAutoSendMessage || !isReady) return;
    sendMessage(pendingAutoSendMessage, {
      hiddenContext: pendingHiddenContext ?? undefined,
      enableWebSearch: pendingEnableWebSearch,
    }).catch((err: unknown) => {
      log.error('AIChat: Failed to send open payload message', err);
    });
    setPendingAutoSendMessage(null);
    setPendingHiddenContext(null);
    setPendingEnableWebSearch(undefined);
  }, [pendingAutoSendMessage, pendingHiddenContext, pendingEnableWebSearch, isReady, sendMessage]);

  // Voice recording disabled for now
  // const { recording, startRecording, stopRecording } = useVoiceRecorder({
  //   send: (buf) => llmSocketInterface.send(buf), // WebSocket, WebRTC, MediaSource, whatever
  //   isReady: true,
  //   addLog: (m) => log.debug(m),
  //   sampleRate: 16000,
  //   bufferSize: 4096,
  // });
  const sendMessageText = useCallback(
    (text: string, trackSend: boolean = false) => {
      if (!text.trim()) return;
      if (!isReady || isLoading) return;
      sendMessage(text, { trackSend }).catch((err: unknown) => {
        log.error('AIChat: Failed to send message', err);
      });
      setInput('');
    },
    [isLoading, isReady, sendMessage],
  );

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Don’t fight the "load older messages" logic
    if (prevScrollHeightRef.current != null) return;
    if (!autoScrollEnabledRef.current) return;

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, streamingMessage]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // First load: just jump to bottom
    if (isInitialLoadRef.current) {
      container.scrollTop = container.scrollHeight;
      isInitialLoadRef.current = false;
      return;
    }

    // After loading older messages: keep viewport anchored
    if (prevScrollHeightRef.current != null && !isLoadingMore) {
      const diff = container.scrollHeight - prevScrollHeightRef.current;
      container.scrollTop += diff;
      prevScrollHeightRef.current = null;
    }
  }, [messages.length, isLoadingMore]);

  const loadOlderMessages = useCallback((): void => {
    if (!hasMore || isLoadingMore || !conversationId) return;

    const container = scrollRef.current;
    if (container) {
      // capture height *before* we prepend
      prevScrollHeightRef.current = container.scrollHeight;
    }

    setPaginationError(null);

    dispatch(loadMoreMessages(conversationId))
      .unwrap()
      .catch((err: unknown) => {
        if (typeof err === 'string' && err === 'No more items to load') {
          return;
        }
        log.error('Failed to load older messages', err);
        setPaginationError('Unable to load older messages.');
      });
  }, [dispatch, hasMore, isLoadingMore, conversationId]);

  const setAutoScrollEnabled = useCallback((enabled: boolean) => {
    if (autoScrollEnabledRef.current === enabled) return;
    autoScrollEnabledRef.current = enabled;
    setIsAutoScrollEnabled(enabled);
  }, []);

  const updateAutoScrollFromUser = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom <= BOTTOM_SCROLL_THRESHOLD;
    setAutoScrollEnabled(isNearBottom);
  }, [setAutoScrollEnabled]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const container = scrollRef.current;
      if (!container) return;

      if (event.nativeEvent.isTrusted) {
        updateAutoScrollFromUser();
      }

      if (container.scrollTop <= TOP_SCROLL_THRESHOLD) {
        loadOlderMessages();
      }
    },
    [loadOlderMessages, updateAutoScrollFromUser],
  );

  const handleScrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setAutoScrollEnabled(true);
    container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [setAutoScrollEnabled]);

  const handleOpenChartDialog = (chart: ChartRequest) => {
    setActiveChart(chart);
  };

  const handleCloseChartDialog = () => {
    setActiveChart(null);
  };

  return (
    <Box className={classes.dialogBorder}>
      <Paper id='simple-chat-interface' elevation={3} className={classes.paper} ref={panelRef}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              order: 1,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <ChatHeader
              onClose={onClose}
              setShowSideBarMenu={handleSetShowSideBarMenu}
              showSideBarMenu={showSideBarMenu}
              handleMinimize={handleMinimize}
              onViewModeChange={onViewModeChange}
              viewMode={viewMode}
              handleRef={handleRef}
              isDefaultState={isDefaultState}
            />

            <Box
              ref={scrollRef}
              onScroll={handleScroll}
              className={`${classes.messageList}${isDefaultState ? ` ${classes.messageListDefaultState}` : ''}`}
            >
              {isPaginating && (
                <Box className={classes.paginationRow} role='status' aria-live='polite'>
                  <CircularProgress size={16} color='inherit' />
                  <Typography variant='captionRegular'>Loading older messages...</Typography>
                </Box>
              )}
              {paginationError && !isLoadingMore && (
                <Box className={classes.paginationError} role='alert'>
                  <Typography variant='captionRegular' color='error'>
                    {paginationError}
                  </Typography>
                  <Button size='small' variant='text' color='error' onClick={loadOlderMessages}>
                    Retry
                  </Button>
                </Box>
              )}
              {isDefaultState && (
                <ChatDefaultState
                  firstName={firstName}
                  greeting={greeting}
                  input={input}
                  isLoading={isLoading}
                  isStreaming={isStreaming}
                  isReady={isReady}
                  setInput={setInput}
                  sendMessageText={sendMessageText}
                />
              )}
              {messages

                .filter(
                  (m) => (m.role === 'user' || m.role === 'assistant') && (m.type === 'message' || m.type === 'error'),
                )
                .map((msg, i) => {
                  const messageKey = msg.id ?? msg.content?.callId ?? i;
                  const isUser = msg.role === 'user';

                  const { text } = msg.content ?? '';
                  const charts: ChartRequest[] = !isUser && msg.type === 'message' ? (msg.content?.charts ?? []) : [];
                  // Disabled relatedQuestions and legal disclaimer for now
                  // const relatedQuestions = metadata?.relatedQuestions ?? [];
                  // const customLegalDisclaimer = metadata?.customLegalDisclaimer ?? false;

                  return (
                    <React.Fragment key={messageKey}>
                      <Box className={isUser ? classes.messageBoxUser : classes.messageBoxAi}>
                        {!isUser && (
                          <Box className={classes.aiAvatarColumn}>
                            <Box sx={{ position: 'relative', zIndex: 0 }}>
                              <AiLogoIcon width={32} height={32} aria-hidden />
                            </Box>
                          </Box>
                        )}
                        <Typography
                          component='div'
                          variant='paragraphRegular'
                          className={isUser ? classes.messageBubbleUser : classes.messageBubbleAi}
                        >
                          {msg.type === 'error' ? (
                            <Typography sx={{ color: theme.palette.error.main }}>{text}</Typography>
                          ) : (
                            <ReactMarkdown
                              remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                              rehypePlugins={[rehypeRaw]}
                              components={markdownComponents}
                            >
                              {text}
                            </ReactMarkdown>
                          )}
                        </Typography>
                      </Box>
                      {!isUser &&
                        charts.map((chart, chartIndex) => (
                          <ChatMessageChartCard
                            key={`${messageKey}-chart-${chartIndex}`}
                            chart={chart}
                            onExpand={handleOpenChartDialog}
                          />
                        ))}
                      {!isUser && msg.type === 'message' && (
                        <Box sx={{ marginLeft: theme.spacing(5), marginTop: theme.spacing(1.5) }}>
                          <ChatFeedback messageId={messageKey} aiResponse={text || ''} charts={charts} />
                        </Box>
                      )}
                      {/* <Box>
                  {customLegalDisclaimer && <Typography>Legal Disclaimer</Typography>}
                  {relatedQuestions?.map((question: string, i: number) => (
                    <Chip
                      sx={{ margin: 0.5 }}
                      variant='outlined'
                      label={question}
                      key={`${question}-${i}`}
                      onClick={() => askRelatedQuestion(question)}
                    />
                  ))}
                </Box> */}
                    </React.Fragment>
                  );
                })}

              {showStatus && (
                <Box className={classes.streamingMessageBox}>
                  <Box className={classes.aiAvatarColumn}>
                    <Box sx={{ position: 'relative', zIndex: 0 }}>
                      <AiLogoIcon width={32} height={32} aria-hidden />
                    </Box>
                  </Box>
                  <ReasoningBlock reasoningMessage={reasoningMessage} />
                </Box>
              )}

              {streamingMessage && (
                <Box className={classes.streamingMessageBox}>
                  <Box className={classes.aiAvatarColumn}>
                    <Box sx={{ position: 'relative', zIndex: 0 }}>
                      <AiLogoIcon width={32} height={32} aria-hidden />
                    </Box>
                  </Box>
                  <Typography component='div' variant='paragraphRegular' className={classes.messageBubbleAi}>
                    <ReactMarkdown
                      remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
                      rehypePlugins={[rehypeRaw]}
                      components={markdownComponents}
                    >
                      {streamingMessage}
                    </ReactMarkdown>
                  </Typography>
                </Box>
              )}

              {error && (
                <Box className={classes.messageBoxAi}>
                  <Typography color='error'>{error}</Typography>
                </Box>
              )}
            </Box>
            {activeChart && <ChartPreviewDialog open onClose={handleCloseChartDialog} chart={activeChart} />}
            {!isDefaultState && (
              <>
                {showScrollToBottom && (
                  <Box className={classes.scrollToBottom}>
                    <IconButton
                      className={classes.scrollToBottomButton}
                      size='small'
                      aria-label='Scroll to bottom'
                      onClick={handleScrollToBottom}
                    >
                      <ArrowDownwardRoundedIcon fontSize='small' />
                    </IconButton>
                  </Box>
                )}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: theme.spacing(12),
                    background: `linear-gradient(to top, ${theme.palette.background.paper} 50%, transparent)`,
                    zIndex: 0,
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
                <ChatControls
                  // recording={recording}
                  // startRecording={startRecording}
                  // stopRecording={stopRecording}
                  input={input}
                  isLoading={isLoading}
                  isStreaming={isStreaming}
                  isReady={isReady}
                  setInput={setInput}
                  sendMessageText={sendMessageText}
                />
              </>
            )}
          </Box>
          <ChatSideBar
            open={showSideBarMenu}
            onClose={handleCloseSideBarMenu}
            persistOnNavigate={persistHistorySidebar}
            viewMode={viewMode}
            persistent={usePersistentFullscreenDrawer}
            containedOverlay={useTemporaryFullscreenDrawer}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default AICHat;
